import { useState, useContext, useEffect } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import Button from './Button';
import DatePicker from './Datepicker';
import PriceDetails from './PriceDetails';
import SelectedDateContext from '../context/SelectedDateContext';
import PriceDetailsContext from '../context/PriceDetailsContext';
import ListingContext from '../context/ListingContext';

type Props = {
  columns: number;
  showPriceDetails?: boolean;
  showBookButton?: boolean;
  numCalendars: number;
  mobileModal?: boolean;
  lastClear?: number;
};

function InlineDatePicker({
  columns = 1,
  numCalendars = 1,
  showPriceDetails,
  showBookButton,
  mobileModal = false,
  lastClear
}: Props) {
  const { selectedDateRange, setSelectedDateRange } = useContext(SelectedDateContext);
  const { isPriceDetailsPending, isPriceDetailsError, priceDetailsData, priceDetailsError } =
    useContext(PriceDetailsContext);

  const { listingId } = useContext(ListingContext);

  const [pickerInstance, setPickerInstance] = useState<any>(null);

  const momentStart = moment(selectedDateRange.start);
  const momentEnd = moment(selectedDateRange.end);

  const numNights =
    selectedDateRange.start && selectedDateRange.end ? momentEnd.diff(momentStart, 'days') : 0;

  const getBookingLink = (): String | null => {
    if (!selectedDateRange.start || !selectedDateRange.end) {
      return null;
    }
    return `https://stay.hideout.house/checkout/${listingId}?checkin=${moment(
      selectedDateRange.start
    ).format(
      'YYYY-MM-DD'
    )}&checkout=${moment(selectedDateRange.end).format('YYYY-MM-DD')}&numberOfGuests=2`;
  };

  useEffect(() => {
    if (pickerInstance && pickerInstance.clear) {
      pickerInstance.clear();
      setSelectedDateRange({ start: null, end: null });
    }
  }, [lastClear]);

  const bookingLink = getBookingLink();

  const handleSelectDate = (dateRange: { start: String | null; end: String | null }) => {
    setSelectedDateRange(dateRange);
  };

  return (
    <InlineDatePickerWrapper>
      <DatePicker
        dateRange={selectedDateRange}
        onSelectDate={handleSelectDate}
        columns={columns}
        numCalendars={numCalendars}
        pickerInstance={pickerInstance}
        setPickerInstance={setPickerInstance}
        mobileModal={mobileModal}
      />
      {showBookButton && (
        <StyledButton externalLink={bookingLink} disabled={!bookingLink} columns={columns}>
          Book Now
        </StyledButton>
      )}
      {showPriceDetails && (
        <PriceDetails
          detailItems={priceDetailsData?.result?.components}
          totalPrice={priceDetailsData?.result?.totalPrice}
          numNights={numNights}
        />
      )}
    </InlineDatePickerWrapper>
  );
}

const InlineDatePickerWrapper = styled.div`
  > input {
    display: none;
  }
  @media screen and (max-width: 480px) {
    .easepick-wrapper {
      width: calc(100% - 40px);
    }
  }

  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const StyledButton = styled(Button)<{ disabled: boolean; columns: number }>`
  margin-top: 20px;
  ${({ columns }) => columns === 1 && 'max-width: 390px;'}
`;

export default InlineDatePicker;
